import { Button, Divider, Flex, Form, Input, Switch, TableProps } from "antd";
import { useEffect } from "react";
import LocationIcon from "../../../../../assets/icons/LocationIcon";
import SearchIcon from "../../../../../assets/icons/SearchIcon";
import LocationModal from "../../../../../components/Organization/LocationModal";
import CustomLoader from "../../../../../components/ui/CustomLoader";
import CustomTable from "../../../../../components/ui/CustomTable";
import {
  useGetAttendanceSettingsQuery,
  useUpsertAttendanceSettingsMutation,
} from "../../../../../features/organization/contractual/AttendanceManagement/attendanceManagementApi";
import { AllShiftParams } from "../../../../../features/organization/contractual/ShiftManagement/shiftManagement.type";
import {
  useGetAllShiftQuery,
  useResetShiftGraceTimeMutation,
  useUpdateShiftGraceTimeMutation,
} from "../../../../../features/organization/contractual/ShiftManagement/shiftManagementApi";
import useError from "../../../../../hooks/useError";
import useModal from "../../../../../hooks/useModal";
import useTable from "../../../../../hooks/useTable";
import { formatDate, parseTime } from "../../../../../lib/time";
import { countParPageSerialNumber } from "../../../../../utils/number";
import { valueOrNa } from "../../../../../utils/string";
import { addKeyInArray } from "../../../../../utils/utils";

const AttendanceSettings = () => {
  const [form] = Form.useForm();
  const { open, handleOpen, handleClose } = useModal();
  const {
    page,
    limit,
    filter,
    keyword,
    handleLimit,
    handleSearch,
    handleFilter,
    handlePagination,
  } = useTable<AllShiftParams>();

  // ------ Watched values ------
  const location = Form.useWatch("geo_location", form);
  const radius = Form.useWatch("radius_meters", form);
  const graceMinutes = Form.useWatch("grace_minutes", form);
  const graceStatus = Form.useWatch("grace_status", form);

  // ------ Queries ------
  const {
    data: settingsResponse,
    isLoading,
    isFetching,
    isSuccess,
  } = useGetAttendanceSettingsQuery({
    params: {},
  });
  const {
    data: allShiftResponse,
    isLoading: shiftLoading,
    isFetching: shiftFetching,
  } = useGetAllShiftQuery({
    params: { ...filter, search: keyword },
  });
  const settingData = settingsResponse?.data?.setting;

  // ------ Mutations ------
  const [
    upsertAttendanceSettings,
    { isLoading: upserting, isError: isUpsertError, error: upsertError },
  ] = useUpsertAttendanceSettingsMutation();

  const [
    resetGraceTime,
    { isLoading: resetting, isError: isResetError, error: resetError },
  ] = useResetShiftGraceTimeMutation();

  const [
    updateGraceTime,
    { isLoading: updating, isError: isUpdateError, error: updateError },
  ] = useUpdateShiftGraceTimeMutation();

  // ------ Handlers ------
  const handleUpdateGraceTime = (values: any) => {
    const {
      shifts,
      radius_meters,
      grace_status,
      grace_minutes,
      geo_status,
      geo_location,
      lat,
      long,
    } = values || {};

    const settingsData = {
      geo_location,
      grace_minutes,
      radius_meters,
      geo_status: geo_status ? "active" : "inactive",
      grace_status: grace_status ? "active" : "inactive",
      lat,
      long,
    };

    upsertAttendanceSettings({ data: settingsData });
    if (shifts?.length) {
      updateGraceTime({ data: shifts });
    }
  };

  // ----- Effects ------
  useEffect(() => {
    if (!settingData && isSuccess) {
      upsertAttendanceSettings({
        data: {
          grace_minutes: 0,
          geo_location: "",
          radius_meters: 0,
          geo_status: "inactive",
          grace_status: "inactive",
        },
      });
    } else if (settingData && isSuccess) {
      const {
        geo_location,
        geo_status,
        grace_minutes,
        grace_status,
        radius_meters,
        lat,
        long,
      } = settingData;

      form.setFieldsValue({
        lat,
        long,
        grace_minutes,
        geo_location,
        radius_meters,
        geo_status: geo_status === "active",
        grace_status: grace_status === "active",
        shifts: allShiftResponse?.data?.shifts?.map((shift: any) => {
          const { id, grace_time_minutes } = shift || {};
          const defaultGraceTime = graceStatus ? grace_minutes : 0;
          return {
            id,
            grade_time_minutes: grace_time_minutes || defaultGraceTime,
          };
        }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allShiftResponse, settingData, isSuccess, form]);

  // ------ Errors ------
  useError(isUpsertError, upsertError, "Attendance settings upsert failed!");
  useError(isUpdateError, updateError, "Shifts grace time update failed!");
  useError(isResetError, resetError, "Shifts grace time reset failed!");

  // ------ Table Columns ------
  const columns: TableProps<any>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Shift Name",
      dataIndex: "shift_name",
      render: (val) => valueOrNa(val),
    },
    {
      align: "center",
      title: "Date Range",
      render: (record) => {
        return `${formatDate(record?.from_date, "DD")}–${formatDate(
          record?.to_date
        )}`;
      },
    },
    {
      align: "center",
      title: "Start Time",
      dataIndex: "shift_start_time",
      render: (val) => parseTime(val),
    },
    {
      align: "center",
      title: "End Time",
      dataIndex: "shift_end_time",
      render: (val) => parseTime(val),
    },
    {
      align: "center",
      title: <span className="text-nowrap">Grace Time (minutes)</span>,
      width: 80,
      dataIndex: "date",
      render: (_, record, index) => {
        return (
          <>
            <Form.Item
              className="mb-0 w-20 !hidden"
              name={["shifts", index, "id"]}
              initialValue={record?.id}
            />
            <Form.Item
              className="mb-0 w-20"
              name={["shifts", index, "grade_time_minutes"]}
            >
              <Input size="middle" placeholder="0" type="number" />
            </Form.Item>
          </>
        );
      },
    },
  ];

  return (
    <Form
      className="custom-form"
      layout="vertical"
      requiredMark="optional"
      size="large"
      onFinish={handleUpdateGraceTime}
      form={form}
    >
      <LocationModal
        handleClose={handleClose}
        open={open?.open}
        data={{
          office_name: location,
          radius: radius,
        }}
        handleManage={(values) => {
          console.log(values);
          form.setFieldsValue({
            geo_location: values.office_name,
            radius_meters: values.radius,
            lat: values?.lat,
            long: values?.lng,
          });
          handleClose();
        }}
      />

      <CustomLoader
        isEmpty={!settingsResponse?.data?.setting}
        isLoading={isLoading || isFetching}
      >
        <div className="p-6 rounded-lg bg-white space-y-4">
          <div className="bg-[#F0F3F9] px-3 py-1.5 rounded-md">
            <h4 className="text-base font-semibold text-display-body">
              General Settings
            </h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-extra-light border-dashed p-4 rounded-md">
              <Form.Item name={"lat"} className="!hidden" />
              <Form.Item name={"long"} className="!hidden" />
              <Form.Item
                label="Grace Period"
                name={"grace_status"}
                className="mb-0 pb-0 h-label"
                layout="horizontal"
                valuePropName="checked"
              >
                <Flex align="center" gap={4}>
                  <Form.Item
                    name="grace_status"
                    valuePropName="checked"
                    noStyle
                  >
                    <Switch />
                  </Form.Item>
                  <span>Activated</span>
                </Flex>
              </Form.Item>
              <Divider className="my-3" />
              <Form.Item
                label="Grace Time (minutes)"
                name={"grace_minutes"}
                className="mb-1.5"
                layout="vertical"
                rules={[
                  { required: true, message: "Please enter Grace Time" },
                  {
                    validator: (_, value) => {
                      if (
                        value === "" ||
                        value === null ||
                        value === undefined
                      ) {
                        // Let the "required" rule handle this case
                        return Promise.resolve();
                      }

                      const num = Number(value);

                      if (!Number.isInteger(num)) {
                        return Promise.reject(
                          "Grace Time (minutes) cannot be decimal"
                        );
                      }
                      if (num < 0) {
                        return Promise.reject(
                          "Grace Time (minutes) cannot be negative"
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input size="large" placeholder="0" type="number" />
              </Form.Item>
              <span className="text-sm text-display-dark">
                Set grace time for office check-in
              </span>
            </div>
            <div>
              <div className="border border-extra-light border-dashed p-4 rounded-md">
                <Form.Item
                  label="GEO Location"
                  name={"geo_status"}
                  className="mb-0 pb-0 h-label"
                  layout="horizontal"
                  valuePropName="checked"
                >
                  <Flex align="center" gap={4}>
                    <Form.Item
                      name="geo_status"
                      valuePropName="checked"
                      noStyle
                    >
                      <Switch />
                    </Form.Item>
                    <span>Activated</span>
                  </Flex>
                </Form.Item>
                <Divider className="my-3" />
                <Flex align="center" gap={10}>
                  <Form.Item
                    label="Add Location"
                    name={"geo_location"}
                    className="mb-0 w-full"
                    layout="vertical"
                  >
                    <Input
                      readOnly
                      size="large"
                      className="cursor-pointer"
                      placeholder="e.g. Uttara Sector 15, Dhaka"
                      onClick={() => {
                        handleOpen({
                          type: "VIEW",
                          data: { office_name: location, radius } as any,
                        });
                      }}
                      suffix={<LocationIcon />}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Radius (Meter)"
                    name={"radius_meters"}
                    className="mb-0"
                    rules={[
                      {
                        validator: (_, value) => {
                          if (
                            value === "" ||
                            value === null ||
                            value === undefined
                          ) {
                            // Let the "required" rule handle this case
                            return Promise.resolve();
                          }

                          const num = Number(value);
                          const regex = /^\d+(\.\d{1,2})?$/;

                          if (num < 0) {
                            return Promise.reject(
                              "Radius (Meter) cannot be negative"
                            );
                          }

                          if (!regex.test(value)) {
                            return Promise.reject(
                              "Radius (Meter) can have up to 2 digits after decimal"
                            );
                          }

                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input size="large" placeholder="0" type="number" />
                  </Form.Item>
                </Flex>
              </div>
            </div>
          </div>
        </div>
      </CustomLoader>

      <div className="p-6 rounded-lg bg-white space-y-4 mt-6">
        <div className="bg-[#F0F3F9] px-3 py-1.5 rounded-md flex items-center justify-between">
          <h4 className="text-base font-semibold text-display-body">
            Shift-based Rules
          </h4>
          <Button
            type="text"
            size="middle"
            loading={resetting}
            onClick={() => {
              resetGraceTime({});
            }}
            className="text-primary hover:!text-primary"
          >
            Reset All
          </Button>
        </div>
        <CustomTable
          loading={shiftLoading || shiftFetching}
          limit={limit}
          page={page}
          columns={columns}
          total={allShiftResponse?.data.total}
          dataSource={addKeyInArray(allShiftResponse?.data.shifts)}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          filterSection={
            <>
              <Input
                size="middle"
                suffix={<SearchIcon />}
                placeholder="Search"
                style={{ minWidth: 200, maxWidth: 250 }}
                onChange={(val) => handleSearch(val)}
                allowClear
              />
            </>
          }
        />
      </div>

      <Flex align="center" gap={16} className="mt-6">
        {/* <Button
          size="middle"
          className="h-10 min-w-[120px]"
       
        >
          Cancel
        </Button> */}
        <Button
          size="middle"
          type="primary"
          htmlType="submit"
          loading={updating}
          className="h-10 min-w-[120px]"
        >
          Save
        </Button>
      </Flex>
    </Form>
  );
};

export default AttendanceSettings;
